import React, { useMemo } from 'react';
import Banner from '../../../../../components/Banner';
import IconAiArrow from '../../../../../components/IconWrapper/arrow-ai';
import usePlatform from '../../../../../hooks/usePlatform';
import { getBannerOperationExtraAttr } from '../../../../../scripts/utils';
import './index.scss';
import '../../../../../components/AIP/aip-banner-wrap.scss';

type NoteTip = { value?: string; label?: string; img?: string };

type FeatureItem = {
  title: string;
  description: string;
};

const srmSectionDiagram1 = '/images/aip/supplierrelations/digital-sourcing-admission.png';
const srmSectionDiagram2 = '/images/aip/supplierrelations/full-process-collaboration.png';
const srmSectionDiagram3 = '/images/aip/supplierrelations/performance-relationship-mgmt.png';
const srmSectionDiagram4 = '/images/aip/supplierrelations/panoramic-risk-management.png';
const case01 = '/images/aip/Retail/case-01.png';
const bannerBg = '/images/aip/supplierrelations/supplierrelations-banner-bg.svg';

type FeatureSectionData = {
  title: string;
  subtitle: string;
  previewAlt: string;
  previewImage: string;
  items: FeatureItem[];
};

const featureSections: FeatureSectionData[] = [
  {
    title: '数字化寻源与准入',
    subtitle: '供应链的“战略寻源库”',
    previewAlt: '数字化寻源与准入示意图',
    previewImage: srmSectionDiagram1,
    items: [
      {
        title: '统一寻源门户',
        description:
          '建立公开的供应商招募与招投标协同平台，流程线上化、阳光化，汇聚更广泛的优质资源。'
      },
      {
        title: '标准化准入流程',
        description:
          '通过线上化的注册、资质提交与多部门协同评审，结合第三方征信数据，建立科学准入标准，实现风险前置化管控。'
      },
      {
        title: '结构化供应商主数据',
        description: '建立统一、规范的供应商信息档案，为后续协同、评估与风险管理奠定数据基础。'
      },
      {
        title: '品类与策略管理',
        description:
          '支持对采购品类进行细分，并制定差异化的寻源策略与供应商管理策略，实现精细化管理。'
      }
    ]
  },
  {
    title: '全流程协同网络',
    subtitle: '供需双方的“在线协作间”',
    previewAlt: '全流程协同网络示意图',
    previewImage: srmSectionDiagram2,
    items: [
      {
        title: '采购执行协同在线化',
        description:
          '实现从订单、交货预约、送货单、质检到对账的全流程线上协同与状态实时同步，全程留痕。'
      },
      {
        title: '供应商协同门户',
        description:
          '为供应商提供统一的业务入口，查看订单、确认交期、更新发货状态、发起对账，提升协同效率与体验。'
      },
      {
        title: '深度协同延伸',
        description:
          '支持共享预测与库存信息（VMI），在线协同处理工程变更（ECN）、质量问题（8D），推动协同向预测与创新层深化。'
      },
      {
        title: '消息与通知中心',
        description: '关键业务状态变更、预警信息自动推送至双方，确保信息同步，及时响应。'
      }
    ]
  },
  {
    title: '绩效与关系管理',
    subtitle: '价值驱动的“量化评估台”',
    previewAlt: '绩效与关系管理示意图',
    previewImage: srmSectionDiagram3,
    items: [
      {
        title: '量化绩效指标体系',
        description:
          '建立覆盖交付（准时率）、质量（合格率）、成本、服务响应等多维度的评估模型。'
      },
      {
        title: '自动化数据采集与考评',
        description:
          '系统自动从ERP、QMS等业务系统采集数据，定期生成客观的供应商绩效评分与综合报告。'
      },
      {
        title: '供应商分级与差异化管理',
        description:
          '基于绩效结果对供应商进行科学分级（战略、杠杆、瓶颈等），实施差异化的合作策略、资源投入与激励措施。'
      },
      {
        title: '持续改进闭环',
        description:
          '支持在线发起改进项目、跟踪改进措施，并将改进结果纳入绩效评估，形成“评估-改进-提升”的正向循环。'
      }
    ]
  },
  {
    title: '全景化风险管理',
    subtitle: '供应链的“智能预警哨”',
    previewAlt: '全景化风险管理示意图',
    previewImage: srmSectionDiagram4,
    items: [
      {
        title: '供应商全景信息视图',
        description:
          '整合资质、绩效、交易、舆情、财务等多源信息，形成360°供应商全景画像，一目了然。'
      },
      {
        title: '智能风险监控与预警',
        description:
          '建立风险模型，对财务状况异常、交货持续延迟、重大负面舆情等风险事件进行7x24小时自动监控与分级预警。'
      },
      {
        title: '业务连续性支持',
        description:
          '基于全面的供应商库和品类信息，快速寻找并评估备用或替代资源，制定业务连续性预案。'
      },
      {
        title: '合规与审计支持',
        description:
          '全程记录供应商引入、考核、交易等操作，满足内审与外部合规要求，实现可追溯性。'
      }
    ]
  }
];

function FeatureSection({ section }: { section: FeatureSectionData }) {
  return (
    <section className="section">
      <div className="container">
        <h2 className="section-title">{section.title}</h2>
        <p className="section-subtitle feature-subtitle">{section.subtitle}</p>
        <div className="feature-split">
          <div className="feature-list">
            {section.items.map((item, index) => (
              <article key={`${section.title}-${item.title}`} className="feature-item feature-item-static">
                <div className="feature-head">
                  <span className="feature-title">{item.title}</span>
                  <span className="feature-index">#{index + 1}</span>
                </div>
                <p className="feature-desc">{item.description}</p>
              </article>
            ))}
          </div>
          <div className="feature-preview">
            <div className="feature-placeholder">
              <img
                className="feature-preview-image"
                src={section.previewImage}
                alt={section.previewAlt}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Supplierrelationships() {
  const { isPc } = usePlatform() || {};

  const bannerConfig = useMemo(() => {
    const src = bannerBg;

    const note =
      '致力于解决采购中供应商寻源不透明、风险应对被动等痛点，通过数字化与系统化管理，将供应商从交易对象转变为可管理、可评估、可协同的战略伙伴。';

    const noteTips: NoteTip[] = [
      { value: '-40%', label: '寻源与准入周期' },
      { value: '+50%', label: '采购协同效率' },
      { value: '+60%', label: '风险主动预见性' }
    ];

    return {
      src,
      mobileSrc: bannerBg,
      title: '业务应用-供应商关系',
      subheading: '',
      legend: '构建透明、高效、韧性的供应体系',
      note,
      noteTips,
      forceShowNote: true,
      operation: [
        {
          text: '立即咨询',
          icon: <IconAiArrow />,
          extraAttr: getBannerOperationExtraAttr({
            jumpBO: { formBOId: 1, formBO: { id: 1 }, jumpContentType: 'FORM' }
          })
        }
      ]
    };
  }, []);

  return (
    <div className="supplierrelationships-page">
      <div className="aip-banner-wrap supplierrelationships-banner">
        <Banner
          showAdvantage={false}
          covered
          useArrow={false}
          useDots={false}
          size="large"
          type={'BANNER_TYPE.CAROUSEL'}
          name={typeof bannerConfig.title === 'string' ? bannerConfig.title : undefined}
          height={isPc ? 700 : 600}
          config={bannerConfig as any}
        />
      </div>

      <main>
        {featureSections.map((section) => (
          <FeatureSection key={section.title} section={section} />
        ))}

        {/* 行业实践案例 - 暂时隐藏 */}
        {/* <section className="section" id="cases">
          <div className="container">
            <h2 className="section-title">行业实践案例</h2>
            <p className="section-subtitle feature-subtitle">智慧零售运营中枢实践案例</p>
            <div className="cases">
              <a className="case-link" href="https://terminus.io/case-detail/2009001" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <span className="case-badge">案例 01</span>
                  <div className="case-body">
                    <h3 className="case-title">智慧零售全渠道业务增长实践</h3>
                    <p className="case-desc">
                      通过一盘货、统一订单中心和客户引擎协同联动，帮助企业提升库存周转效率、履约人效与会员复购表现。
                    </p>
                    <div className="case-illustration">
                      <img className="case-illustration-img" src={case01} alt="零售案例配图" loading="lazy" />
                    </div>
                  </div>
                </article>
              </a>
            </div>
          </div>
        </section> */}
      </main>
    </div>
  );
}
